import React, { lazy, Suspense, useEffect, useMemo } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { bffAuthService } from './services/BffAuthService';
import { useAppManager } from './hooks/managers/useAppManager';
import { useUserManager, useUserStoreData } from './hooks/managers/useUserManager';
import { useUserStore } from './stores/userStore';
import { useUserWebSocket } from './hooks/useUserWebSocket';
import LoginPage from './home/shared/LoginPage';
import { ROUTES } from './home/constants';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';

import * as locales from '@mui/material/locale';
import { MUI_LANGUAGE_MAP } from './common/MetaSetting';
import { createAppTheme } from './common/ThemeColors';
import { ColorModeProvider, useColorMode } from './common/ColorModeContext';
import { NotFound } from './NotFound';
import { MLayout } from './home/shared/MLayout';
import { ALayout } from './home/shared/ALayout';
import { BffSecure } from './home/shared/BffSecure';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useGlobalNotificationHandler } from './hooks/managers/useGlobalNotificationHandler';
import { useGlobalWebSocket } from './hooks/useGlobalWebSocket';
import { PageLoading } from './components/PageLoading';
import { ErrorBoundary } from './components/ErrorBoundary';

// 路由级代码分割：页面与重型依赖（编辑器/echarts/IM 等）按需加载，缩小首屏包体
const SecureDashboard = lazy(() => import('./home/pages/Dashboard'));
const SecureEditPage = lazy(() => import('./home/pages/EditPage'));
const SecureMessagesPage = lazy(() => import('./home/pages/MessagesPage'));
const SecureSettingsPage = lazy(() => import('./home/pages/SettingsPage'));
const MemoView = lazy(() =>
  import('./home/components/memoView/MemoView').then((m) => ({ default: m.MemoView }))
);

const AppContent = () => {
  const { t, i18n } = useTranslation();
  const { currentLanguage, changeCurrentLanguage } = useAppManager();
  const { mode } = useColorMode();
  // 主题随语言与深色模式重建（04-1.1）
  const themeWithLocale = useMemo(() => createTheme(createAppTheme(mode), locales[MUI_LANGUAGE_MAP[currentLanguage]]), [currentLanguage, mode]);
  const { enqueueSnackbar } = useSnackbar();
  
  const isAuthenticated = bffAuthService.isAuthenticated;

  useEffect(() => {
    const browserLang = i18n.language || navigator.language || navigator.languages[0];
    const detectedLang = browserLang.startsWith('zh') ? 'zh' : 'en';
    
    if (currentLanguage !== detectedLang) {
      changeCurrentLanguage(detectedLang);
      i18n.changeLanguage(detectedLang);
    }
  }, []);

  const { fetchUserAsync } = useUserManager();
  const { currentUser } = useUserStoreData();
  const { connect: connectWebSocket, disconnect: disconnectWebSocket } = useGlobalWebSocket();
  const { connect: connectUserWs, disconnect: disconnectUserWs } = useUserWebSocket();

  useGlobalNotificationHandler();

  useEffect(() => {
    let cancelled = false;

    const ensureUserId = async (): Promise<number | null> => {
      if (currentUser?.userId) {
        return currentUser.userId;
      }

      let userInfo = bffAuthService.getUserInfo();
      if (!userInfo) {
        userInfo = await bffAuthService.fetchUserInfo();
      }

      if (userInfo?.sub) {
        const numericUserId = Number(userInfo.sub);
        if (currentUser && !currentUser.userId) {
          useUserStore.getState().setCurrentUser({
            ...currentUser,
            userId: numericUserId
          });
        }
        return numericUserId;
      }

      return null;
    };

    const doConnect = async () => {
      if (cancelled) return;
      if (!bffAuthService.isAuthenticated || !currentUser?._id) {
        return;
      }

      const userId = await ensureUserId();

      if (cancelled) return;
      if (userId) {
        connectWebSocket(userId);
        connectUserWs();
      }
    };

    const handleAuthChange = async (authenticated: boolean) => {
      if (cancelled) return;

      if (authenticated) {
        await doConnect();
      } else {
        disconnectWebSocket();
        disconnectUserWs();
      }
    };

    doConnect();

    const unsubscribe = bffAuthService.subscribe(handleAuthChange);

    const handleBeforeUnload = () => {
      disconnectWebSocket();
      disconnectUserWs();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      disconnectWebSocket();
      disconnectUserWs();
    };
  }, [currentUser?.userId, currentUser?._id, isAuthenticated, connectWebSocket, disconnectWebSocket, connectUserWs, disconnectUserWs]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserAsync();
    } else {
      const { clear } = useUserStore.getState();
      clear();
    }
  }, [isAuthenticated, fetchUserAsync]);

  // 页面标题跟随语言；document.title 始终存在，不依赖 index.html 中的 DOM 元素
  // （原实现依赖 <title id="title">，该 id 在 5b320629 改为静态标题时被移除导致 null 崩溃）
  document.title = t('webTitle');

  return (
    <ThemeProvider theme={themeWithLocale}>
      {/* 全局错误边界：渲染异常不再导致整站白屏（05-5.13） */}
      <ErrorBoundary variant="page">
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path={ROUTES.index} element={<Navigate to={ROUTES.dashboard} />} />
            <Route path={ROUTES.view} element={<ALayout />}>
              <Route index element={<Navigate to={ROUTES.memo} />} />
              {/* 阅读页单独降级为卡片，分享场景不能白屏 */}
              <Route
                path={ROUTES.memo}
                element={
                  <ErrorBoundary variant="card">
                    <MemoView />
                  </ErrorBoundary>
                }
              />
            </Route>
            <Route path={ROUTES.home} element={<MLayout />}>
              <Route index element={<Navigate to={ROUTES.dashboard} />} />
              <Route path={ROUTES.dashboard} element={<SecureDashboard />} />
              <Route path={ROUTES.edit} element={<SecureEditPage />} />
              <Route path={ROUTES.messages} element={<SecureMessagesPage />} />
              <Route path={ROUTES.settings} element={<SecureSettingsPage />} />
            </Route>
            <Route path="/login" element={<LoginPage />} />
            <Route path={ROUTES.other} element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export const App = () => {
  return (
    <SnackbarProvider maxSnack={5} dense>
      <BffSecure>
        <ColorModeProvider>
          <AppContent />
        </ColorModeProvider>
      </BffSecure>
    </SnackbarProvider>
  );
};

export default App;
